import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import dayjs, { type Dayjs } from 'dayjs';
import { $postInquiryStore } from '@lib/store';
import { cn, DateUtils, LsbUtils } from '@shared/utils';
import { InquiryPreferredMethod, InquiryServicesRequired, InquiryUrgency, type InquiryJson } from '@shared/api';
import { BaseInput, Button, DateInput, Multiselect, MultiselectVariant, Select, Spinner, Textarea } from '@shared/widgets';

const TODAY = dayjs().startOf('day');

interface TravelRequestForm {
  servicesRequired: InquiryServicesRequired[];
  numberOfTravellers: string;
  from: string;
  to: string;
  departureDate?: Dayjs;
  returnDate?: Dayjs;
  requirements: string;
  preferredContactMethod: InquiryPreferredMethod;
  urgency: InquiryUrgency;
}
const DEFAULT_VALUES: TravelRequestForm = {
  servicesRequired: [],
  numberOfTravellers: '',
  from: '',
  to: '',
  departureDate: undefined,
  returnDate: undefined,
  requirements: '',
  preferredContactMethod: InquiryPreferredMethod.Email,
  urgency: InquiryUrgency.Normal,
};

interface TravelFormProps {
  className?: string;
  onSubmit?: () => void;
}

export const TravelForm = (props: TravelFormProps) => {
  const { control, formState, getValues, handleSubmit, reset, setValue, watch } = useForm<TravelRequestForm>({ defaultValues: DEFAULT_VALUES });
  const departureDate = watch('departureDate');

  const { t } = useTranslation('travel');

  const submitHandler = async (data: TravelRequestForm) => {
    const response = await $postInquiryStore.events.postInquiry({
      json: JSON.stringify({
        servicesRequired: data.servicesRequired,
        numberOfTravellers: +data.numberOfTravellers,
        from: data.from,
        to: data.to,
        departureDate: data.departureDate ? DateUtils.formatToIsoString(data.departureDate) : '-',
        returnDate: data.returnDate ? DateUtils.formatToIsoString(data.returnDate) : '-',
        requirements: data.requirements,
        preferredContactMethod: data.preferredContactMethod,
        urgency: data.urgency,
      } satisfies InquiryJson),
      description: [
        `Services required: ${data.servicesRequired.join(', ')}`,
        `Number of travellers: ${data.numberOfTravellers}`,
        `From: ${data.from}`,
        `To: ${data.to}`,
        `Departure date: ${data.departureDate ? data.departureDate.format('DD/MM/YY') : '-'}`,
        `Return date: ${data.returnDate ? data.returnDate.format('DD/MM/YY') : '-'}`,
        `Requirements: ${data.requirements}`,
        `Urgency: ${LsbUtils.getUrgencyLabel(data.urgency)}`,
      ].join('\n'),
      urgency: data.urgency,
      preferredMethod: data.preferredContactMethod,
    });
    if (response.success) {
      props.onSubmit?.();
      reset(DEFAULT_VALUES);
    }
  };

  const SERVICE_OPTIONS = [
    { label: t('travelForm.serviceOptions.hotels'), value: InquiryServicesRequired.Hotels },
    { label: t('travelForm.serviceOptions.flights'), value: InquiryServicesRequired.Flights },
    { label: t('travelForm.serviceOptions.transfers'), value: InquiryServicesRequired.Transfers },
    { label: t('travelForm.serviceOptions.restaurants'), value: InquiryServicesRequired.Restaurants },
    { label: t('travelForm.serviceOptions.carRental'), value: InquiryServicesRequired.CarRental },
    { label: t('travelForm.serviceOptions.vipAirport'), value: InquiryServicesRequired.VipAirport },
  ];
  const PREFERRED_CONTACT_METHOD_OPTIONS = [
    { label: t('travelForm.preferredContactMethodOptions.email'), value: InquiryPreferredMethod.Email },
    { label: t('travelForm.preferredContactMethodOptions.phone'), value: InquiryPreferredMethod.Phone },
  ];
  const URGENCY_OPTIONS = [
    { label: t('travelForm.urgencyOptions.normal'), value: InquiryUrgency.Normal },
    { label: t('travelForm.urgencyOptions.urgent'), value: InquiryUrgency.Urgent },
  ];
  return (
    <form className={cn('flex flex-col gap-4', props.className)} noValidate onSubmit={handleSubmit(submitHandler)}>
      <Spinner show={formState.isSubmitting} position />
      <div className={cn('flex gap-2', 'max-lg:flex-col max-lg:gap-4')}>
        <Controller
          name='servicesRequired'
          control={control}
          rules={{ required: t('errors.form.required', { ns: 'common' }) }}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <Multiselect
              className='w-full'
              variant={MultiselectVariant.Chips}
              label={t('travelForm.labels.services')}
              nullValue={{ isEnable: false, text: t('travelForm.placeholders.services') }}
              items={SERVICE_OPTIONS}
              required
              error={error?.message}
              value={value}
              onChange={onChange}
            />
          )}
        />
        <Controller
          name='numberOfTravellers'
          control={control}
          rules={{ required: t('errors.form.required', { ns: 'common' }) }}
          render={({ field, fieldState: { error } }) => (
            <BaseInput
              {...field}
              className={cn('basis-1/3', 'max-lg:basis-auto')}
              label={t('travelForm.labels.numberOfTravellers')}
              placeholder={t('travelForm.placeholders.numberOfTravellers')}
              type='number'
              onlyPositive
              onlyNumeric
              required
              error={error?.message}
            />
          )}
        />
      </div>
      <div className={cn('flex gap-2', 'max-lg:flex-col max-lg:gap-4')}>
        <Controller
          name='from'
          control={control}
          rules={{ required: t('errors.form.required', { ns: 'common' }) }}
          render={({ field, fieldState: { error } }) => (
            <BaseInput
              {...field}
              label={t('travelForm.labels.from')}
              placeholder={t('travelForm.placeholders.from')}
              required
              error={error?.message}
            />
          )}
        />
        <Controller
          name='to'
          control={control}
          rules={{ required: t('errors.form.required', { ns: 'common' }) }}
          render={({ field, fieldState: { error } }) => (
            <BaseInput {...field} label={t('travelForm.labels.to')} placeholder={t('travelForm.placeholders.to')} required error={error?.message} />
          )}
        />
      </div>
      <div className={cn('flex gap-2', 'max-lg:flex-col max-lg:gap-4')}>
        <Controller
          name='departureDate'
          control={control}
          rules={{ required: t('errors.form.required', { ns: 'common' }) }}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <DateInput
              label={t('travelForm.labels.departureDate')}
              required
              error={error?.message}
              minDate={TODAY}
              value={value}
              onChange={(next) => {
                onChange(next);
                const returnDate = getValues('returnDate');
                if (next && returnDate?.startOf('day').isBefore(next.startOf('day'))) setValue('returnDate', undefined);
              }}
            />
          )}
        />
        <Controller
          name='returnDate'
          control={control}
          rules={{ required: t('errors.form.required', { ns: 'common' }) }}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <DateInput
              label={t('travelForm.labels.returnDate')}
              required
              error={error?.message}
              minDate={departureDate?.startOf('day') ?? TODAY}
              value={value}
              onChange={onChange}
            />
          )}
        />
      </div>
      <Controller
        name='requirements'
        control={control}
        rules={{ required: t('errors.form.required', { ns: 'common' }) }}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <Textarea
            label={t('travelForm.labels.requirements')}
            placeholder={t('travelForm.placeholders.requirements')}
            required
            error={error?.message}
            value={value}
            onChange={onChange}
          />
        )}
      />
      <div className={cn('flex gap-2', 'max-lg:flex-col max-lg:gap-4')}>
        <Controller
          name='preferredContactMethod'
          control={control}
          rules={{ required: t('errors.form.required', { ns: 'common' }) }}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <Select
              className='w-full'
              label={t('travelForm.labels.preferredContactMethod')}
              nullValue={{ isEnable: false, text: t('travelForm.placeholders.preferredContactMethod') }}
              items={PREFERRED_CONTACT_METHOD_OPTIONS}
              required
              error={error?.message}
              value={value}
              onChange={onChange}
            />
          )}
        />
        <Controller
          name='urgency'
          control={control}
          rules={{ required: t('errors.form.required', { ns: 'common' }) }}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <Select
              className='w-full'
              label={t('travelForm.labels.urgency')}
              nullValue={{ isEnable: false, text: t('travelForm.placeholders.urgency') }}
              items={URGENCY_OPTIONS}
              required
              error={error?.message}
              value={value}
              onChange={onChange}
            />
          )}
        />
      </div>
      <Button fullWidth color='primary' type='submit' disabled={formState.isSubmitting}>
        {t('travelForm.button')}
      </Button>
    </form>
  );
};
